/* eslint-disable id-blacklist */
import { CurrencyPipe } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import {
  CustomerServiceNote,
  CustomerServiceNoteService,
  FareReviewStatusUpdate,
  FareService,
  ItineraryService,
  Journey,
  LyftDispatchRideFareReviewStatusUpdate,
  LyftService,
  OrganizationRiderService,
  OrganizationService,
  PaymentAccountService,
  Reservation,
  ReservationService,
  RideRequestDetail,
  RideRequestService,
  RiderFareErrorConstants,
  RideUtil,
  RuleValueService,
  TransitProgramService,
  Trip
} from '@uzurv/clara';
import {
  ApplicationException,
  ExecutionPlatform,
  FareReviewStatusConstants,
  isNullOrUndefined,
  ObjectConstants,
  PermissionsConstants,
  ReservationStatusNameConstants,
  ReservationStatusOrder,
  RoutePeriodCodeConstants,
  SafeUrlPipe,
  VehicleTypeConstants
} from '@uzurv/core';
import { DriverService as DotsDriverService } from '@uzurv/dots';
import { AuthDataService } from '@uzurv/http';
import { interval, Observable, of, take } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { APP_CONFIGURATION, AppConfiguration } from '../../app/app-configuration';
import { AdjustReservationStatusDialogComponent } from '../../components/dialogs/adjust-reservation-status-dialog/adjust-reservation-status-dialog.component';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { DriverFareCalculationHistoryDailogComponent } from '../../components/dialogs/driver-fare-calulation-history-dialog/driver-fare-calculation-history-dialog.component';
import { FareAndRatesDetailsDailogComponent } from '../../components/dialogs/fare-and-rates-details-dialog/fare-and-rates-details-dialog.component';
import { RiderFareCollectionDialogComponent } from '../../components/dialogs/rider-fare-collection-dialog/rider-fare-collection-dialog.component';
import { SelectDriverDialogComponent } from '../../components/dialogs/select-driver-dialog/select-driver-dialog.component';
import { SelectProviderDialogComponent } from '../../components/dialogs/select-provider-dialog/select-provider-dialog.component';
import { TripNotesDialogComponent } from '../../components/dialogs/trip-notes-dialog/trip-notes-dialog.component';
import { ReservationEventService, ReservationListItemVm } from '../../components/reservation';
import { ErrorTranslationService } from '../../services/error-translation/error-translation.service';
import * as TripDetailActions from './trip-detail.actions';
import { TripDetailState } from './trip-detail.reducer';
import {
  getFareReviewDetail,
  getFareReviewNotes,
  getNonacceptedRidesParams,
  getTrip,
  getTripDetailState,
  isEligibleForFareAdjustment
} from './trip-detail.selectors';
import { TripService } from '../../services/trip/trip.service';
import { RideCancellationService } from '../../services/trip/ride-cancellation.service';
import { TabUtils } from '../../services/tab/tab.service';
import { LYFT_PARAMS_KEY } from '../../pages/lyft/lyft.page';
import {
  AdjustedProviderCommissionDeviationState,
  ProviderCommissionAdjustmentThresholds,
  ProviderCommissionAdjustmentValidationService
} from '../../services/provider-commission-adjustment-validation/provider-commission-adjustment-validation.service';

@Injectable()
export class TripDetailEffects {
  openTripDetailTab$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenTripDetailTab),
        map(action => {
          if (action.payload.trip.driverName) {
            this.router.navigate([`/rides/res-${action.payload.trip.reservationId}`]);
          } else {
            this.router.navigate([`/rides/rr-${action.payload.trip.rideRequestId}`]);
          }
        })
      );
    },
    { dispatch: false }
  );

  openTripDetailTabByReservationId$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenTripDetailTabByReservationId),
        map(action => {
          this.router.navigate(
            [`/rides/res-${action.payload.reservationId}`],
            action.payload.tabselected ? { queryParams: { tabselected: action.payload.tabselected } } : null
          );
          this.store$.dispatch(TripDetailActions.GetTripDetail({ payload: { id: action.payload.reservationId, type: 'res' } }));
        })
      );
    },
    { dispatch: false }
  );

  getTripInfoFromNavigation$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailFromUrl),
      switchMap(action => {
        const { rideTypeString, rideId } = this.getRideByUrlFragment(action.fullRoute);
        return of(TripDetailActions.GetTripDetail({ payload: { id: rideId, type: rideTypeString } }));
      })
    );
  });

  getSelectedTabIndex$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      map(() => this.router.routerState.snapshot.root.queryParams['tabselected']),
      filter(tabselected => !isNullOrUndefined(tabselected)),
      map(tabselected => TripDetailActions.SetSelectedTabIndex({ index: +tabselected }))
    );
  });

  getTrip$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetail),
      switchMap(action => {
        if (action.payload.type === 'rr') {
          return this.rideRequestService.findById(action.payload.id);
        } else {
          return this.reservationService.findReservationById(action.payload.id);
        }
      }),
      request =>
        request.pipe(
          map(trip => {
            // If the ride doesn't exist
            if (isNullOrUndefined(trip)) {
              return TripDetailActions.GetTripDetailFail({ error: new ApplicationException('Ride not found') });
            }

            // If the we need to show the reservation instead of the ride request
            if (trip instanceof RideRequestDetail && trip.accepted) {
              return TripDetailActions.SwitchToActiveReservation({ payload: trip.id });
            }

            return TripDetailActions.GetTripDetailSuccess({ payload: trip });
          }),
          catchError(error => of(TripDetailActions.GetTripDetailFail({ error })))
        )
    );
  });

  switchToActiveReservation$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SwitchToActiveReservation),
        switchMap(action =>
          this.rideRequestService.findActiveReservationByRideRequestId(action.payload).pipe(
            map(reservation => {
              // If no active reservation is found do not update the tab
              if (!isNullOrUndefined(reservation)) {
                history.replaceState({}, '', `rides/res-${reservation.id}`);
                this.store$.dispatch(TripDetailActions.GetTripDetailSuccess({ payload: reservation }));
              }
            }),
            catchError(error =>
              of(
                TripDetailActions.GetTripDetailFail({
                  error: new ApplicationException('Could not find Active Reservation for Ride Request')
                })
              )
            )
          )
        )
      );
    },
    { dispatch: false }
  );

  setPermissions$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      map(() =>
        TripDetailActions.SetPermissions({
          payload: {
            canEditBasicTrip: this.authDataService.hasPermission({ all: [PermissionsConstants.RIDE_REQUEST_UPDATE], any: [] }),
            canEditWavTrip: this.authDataService.hasPermission({ all: [PermissionsConstants.PROVIDER_RIDE_REQUEST_UPDATE], any: [] })
          }
        })
      )
    );
  });

  getFareReviewStatus$ = createEffect(() => {
    return this.action$.pipe(
      ofType(
        TripDetailActions.GetFareReviewStatus,
        TripDetailActions.UpdateFareReviewStatusRequiredSuccess,
        TripDetailActions.ShowFareReviewEditModeSuccess,
        TripDetailActions.CancelFareAdjustmentSuccess,
        TripDetailActions.SubmitFareAdjustmentSuccess,
        TripDetailActions.ShowLyftReviewEditModeSuccess,
        TripDetailActions.CancelLyftReviewSuccess,
        TripDetailActions.SubmitLyftReviewSuccess,
        TripDetailActions.GetTripDetailSuccess,
        TripDetailActions.RestoreState
      ),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(
        ([action, state]) =>
          !isNullOrUndefined(state.trip) &&
          this.authDataService.hasPermission({ any: ['fare_review'], all: [] }) &&
          isEligibleForFareAdjustment(state)
      ),
      switchMap(([_, state]) =>
        this.fareService
          .getFareStatusForReservation(state.trip.id)
          .pipe(map(fareReviewStatus => TripDetailActions.GetFareReviewStatusSuccess({ fareReviewStatus })))
      )
    );
  });

  getFareData$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GetFareReviewStatusSuccess),
        withLatestFrom(this.store$.select(getTripDetailState)),
        map(([_, tripDetailState]) => {
          if (tripDetailState.fareReviewStatus) {
            if (tripDetailState.fareReviewStatus.executionPlatform === ExecutionPlatform.UZURV360) {
              if (
                tripDetailState.fareReviewStatus.fareReviewStatus !== FareReviewStatusConstants.AWAITING_CALCULATION &&
                tripDetailState.fareReviewStatus.fareReviewStatus !== FareReviewStatusConstants.NOT_ELIGIBLE &&
                tripDetailState.fareReviewStatus.fareReviewStatus !== FareReviewStatusConstants.AWAITING_TOLL_CONFIRMATION
              ) {
                this.store$.dispatch(TripDetailActions.GetFareReviewDetail());
              }
            } else {
              if (
                tripDetailState.fareReviewStatus.fareReviewStatus === FareReviewStatusConstants.REQUIRED ||
                tripDetailState.fareReviewStatus.fareReviewStatus === FareReviewStatusConstants.IN_PROGRESS
              ) {
                this.store$.dispatch(TripDetailActions.FindActiveByReservationId());
                if (!tripDetailState.lyftDispatchRideFareAdjustmentReasons) {
                  this.store$.dispatch(TripDetailActions.GetLyftDispatchRideFareAdjustmentReasons());
                }
              }
            }
          }
        })
      );
    },
    { dispatch: false }
  );

  getFareDetail$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetFareReviewDetail, TripDetailActions.GetLyftDispatchRideFareAdjustmentReasonsSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([_, trip]) => !isNullOrUndefined(trip)),
      switchMap(([_, trip]) =>
        this.fareService.getFareReviewDetail(trip.id).pipe(
          map(
            fareReviewDetail => TripDetailActions.GetFareReviewDetailSuccess({ fareReviewDetail }),
            catchError(err => of(null))
          )
        )
      )
    );
  });

  getLyftReceipt$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.FindActiveByReservationIdSuccess),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(([action, state]) => state.trip.rideRequestDetail.executeOnLyftDispatch),
      switchMap(([_, state]) =>
        this.lyftService
          .getRideReceipt(state.lyftDispatchRide.id)
          .pipe(map(receipt => TripDetailActions.GetLyftDispatchRideReceiptSuccess({ lyftDispatchRideReceipt: receipt })))
      )
    );
  });

  getLyftDispatchRideFareAdjustmentReasons$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetLyftDispatchRideFareAdjustmentReasons),
      switchMap(action =>
        this.fareService
          .listAllLyftDispatchRideFareAdjustmentReasons()
          .pipe(
            map(lyftDispatchRideFareAdjustmentReasons =>
              TripDetailActions.GetLyftDispatchRideFareAdjustmentReasonsSuccess({ lyftDispatchRideFareAdjustmentReasons })
            )
          )
      )
    );
  });

  getLyftDispatchRide$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.FindActiveByReservationId),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([_, trip]) =>
        this.lyftService
          .findActiveByReservationId(trip.id)
          .pipe(map(lyftDispatchRide => TripDetailActions.FindActiveByReservationIdSuccess({ lyftDispatchRide })))
      )
    );
  });

  refreshTrip$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.RefreshTripDetail, TripDetailActions.PatchRideRequestSuccess),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(([action, state]) => !state.editMode && !(state.trip.isCompleted || state.trip.isCancelled)),
      switchMap(([action, state]) => {
        if (state.trip.isReservation) {
          return this.reservationService.findReservationById(state.trip.id);
        } else {
          return this.rideRequestService.findById(state.trip.rideRequestDetail.id);
        }
      }),
      request =>
        request.pipe(
          map(trip => {
            if (isNullOrUndefined(trip)) {
              return TripDetailActions.GetTripDetailFail({ error: new ApplicationException('Ride not found') });
            }
            // If the we need to show the reservation instead of the ride request
            if (trip instanceof RideRequestDetail && trip.accepted) {
              return TripDetailActions.SwitchToActiveReservation({ payload: trip.id });
            }
            return TripDetailActions.GetTripDetailSuccess({ payload: trip });
          }),
          catchError(error => of(TripDetailActions.GetTripDetailFail({ error })))
        )
    );
  });

  getDriverProfileImage$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GetTripDetailSuccess),
        withLatestFrom(this.store$.select(getTrip)),
        filter(([action, trip]) => !isNullOrUndefined(trip)),
        map(([action, trip]) => {
          if (trip.driver && trip.driver.emailAddress !== 'lyft_dispatch_driver' && !isNullOrUndefined(trip.driver.dotsDriverIdentifier)) {
            this.dotsDriverService.getProfilePictureForDriver(trip.driver.dotsDriverIdentifier).subscribe(
              fileData =>
                this.store$.dispatch(
                  TripDetailActions.GetDriverProfilePictureSuccess({
                    safeUrl: fileData && fileData.getDataURLString() ? this.safeUrlPipe.transform(fileData.getDataURLString()) : null
                  })
                ),
              error => this.store$.dispatch(TripDetailActions.GetDriverProfilePictureFail({ error }))
            );
          }
        })
      );
    },
    { dispatch: false }
  );

  gerRiderPaymentAccountInfo$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([action, trip]) => !isNullOrUndefined(trip) && !isNullOrUndefined(trip.rideRequestDetail.riderPaymentAccountId)),
      switchMap(([action, trip]) =>
        this.organizationRiderService
          .findPaymentAccountById(trip.rideRequestDetail.organizationRider.id, trip.rideRequestDetail.riderPaymentAccountId)
          .pipe(
            map(riderPaymentAccount => TripDetailActions.GetRiderPaymentAccountSuccess({ payload: riderPaymentAccount })),
            catchError(error => of(TripDetailActions.GetRiderPaymentAccountFail({ error })))
          )
      )
    );
  });

  getRiderIgnoredDrivers$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([action, trip]) => !isNullOrUndefined(trip)),
      switchMap(([action, trip]) =>
        this.organizationRiderService.listIgnoredDriversByOrganizationRiderId(trip.rideRequestDetail.organizationRider.id).pipe(
          map(ignoredDrivers => TripDetailActions.GetRiderIgnoredDriversSuccess({ drivers: ignoredDrivers })),
          catchError(error => of(TripDetailActions.GetRiderIgnoredDriversFail({ error })))
        )
      )
    );
  });

  getEnRouteRouteSummaryForCancelledReservation$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(([action, state]) => {
        return (
          !isNullOrUndefined(state.trip) &&
          state.trip.cancelled &&
          ReservationStatusOrder[state.trip.reservationStatus.name] >=
            ReservationStatusOrder[ReservationStatusNameConstants.DRIVER_EN_ROUTE]
        );
      }),
      switchMap(([action, state]) => {
        return this.reservationService
          .findReservationRouteSummaryByReservationId(state.trip.id, RoutePeriodCodeConstants.PERIOD2)
          .pipe(map(reservationRouteSummary => TripDetailActions.GetRouteSummaryForPeriod2Success({ reservationRouteSummary })));
      })
    );
  });

  getCompletedRoute$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(
        ([action, trip]) =>
          !isNullOrUndefined(trip) &&
          trip.rideRequestDetail.accepted &&
          (trip.isCompleted || (trip.isCancelled && trip.reservationStatus.name === ReservationStatusNameConstants.IN_PROGRESS)) &&
          trip.driver.emailAddress !== 'lyft_dispatch_driver'
      ),
      switchMap(([action, trip]) =>
        this.reservationService.findReservationRouteSummaryByReservationId(trip.id, RoutePeriodCodeConstants.PERIOD3).pipe(
          map(reservationRouteSummary => TripDetailActions.GetCompletedRouteSuccess({ reservationRouteSummary })),
          catchError(error => of(TripDetailActions.GetCompletedRouteFail({ reservationRouteSummary: error })))
        )
      )
    );
  });

  favoriteDriver$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.FavoriteDriver),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.organizationRiderService.addFavoriteDriverToRiderWithId(trip.rideRequestDetail.organizationRider.id, trip.driver.id).pipe(
          map(() => TripDetailActions.FavoriteDriverSuccess()),
          catchError(error => of(TripDetailActions.FavoriteDriverFail({ error })))
        )
      )
    );
  });

  removeFavoritedDriver$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.RemoveFavoritedDriver),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.organizationRiderService.removeFavoriteDriverFromRiderWithId(trip.rideRequestDetail.organizationRider.id, trip.driver.id).pipe(
          map(() => TripDetailActions.RemoveFavoritedDriverSuccess()),
          catchError(error => of(TripDetailActions.RemoveFavoritedDriverFail({ error })))
        )
      )
    );
  });

  ignoreDriver$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.IgnoreDriver),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.organizationRiderService.addIgnoredDriverToRider(trip.rideRequestDetail.organizationRider.id, trip.driver.id).pipe(
          map(() => TripDetailActions.IgnoreDriverSuccess()),
          catchError(error => of(TripDetailActions.IgnoreDriverFail({ error })))
        )
      )
    );
  });

  removeIgnoredDriver$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.RemoveIgnoredDriver),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.organizationRiderService.removeIgnoredDriverFromRider(trip.rideRequestDetail.organizationRider.id, trip.driver.id).pipe(
          map(() => TripDetailActions.RemoveIgnoredDriverSuccess()),
          catchError(error => of(TripDetailActions.RemoveIgnoredDriverFail({ error })))
        )
      )
    );
  });

  getFareReviewNotes$ = createEffect(() => {
    return this.action$.pipe(
      ofType(
        TripDetailActions.GetFareReviewDetailSuccess,
        TripDetailActions.GetFareReviewNotes,
        TripDetailActions.AddFareReviewNoteSuccess
      ),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([_, state]) => {
        const customerServiceNoteObjectName = ObjectConstants.FARE_CALCULATION;
        const customerServiceNoteObjectId = state.fareReviewDetail.fareCalculationId;

        return this.customerServiceNoteService
          .getCustomerServiceNotesForObjectIdAndName(customerServiceNoteObjectId, customerServiceNoteObjectName)
          .pipe(
            map(
              fareReviewNotes => TripDetailActions.GetFareReviewNotesSuccess({ fareReviewNotes }),
              catchError(error => of(TripDetailActions.GetNotesFail({ error })))
            )
          );
      })
    );
  });

  addFareReviewNote$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.AddFareReviewNote),
      withLatestFrom(this.store$.select(getFareReviewDetail)),
      switchMap(([action, fareReviewDetail]) => {
        const fareReviewNote = new CustomerServiceNote();
        fareReviewNote.objectName = ObjectConstants.FARE_CALCULATION;
        fareReviewNote.objectId = fareReviewDetail.fareCalculationId;
        fareReviewNote.text = action.fareReviewNoteText;

        return this.customerServiceNoteService.createCustomerServiceNote(fareReviewNote).pipe(
          map(note => TripDetailActions.AddFareReviewNoteSuccess({ fareReviewNote: note })),
          catchError(error => of(TripDetailActions.AddNoteFail({ error })))
        );
      })
    );
  });

  getNotes$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess, TripDetailActions.AddNoteSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(
        ([action, trip]) =>
          !isNullOrUndefined(trip) && this.authDataService.hasPermission({ all: [PermissionsConstants.CUSTOMER_SERVICE_NOTE_READ] })
      ),
      switchMap(([action, trip]) => {
        let customerServiceNoteObjectId: number;
        let customerServiceNoteObjectName: string;

        if (trip.isReservation) {
          customerServiceNoteObjectName = ObjectConstants.RESERVATION;
          customerServiceNoteObjectId = trip.id;
        } else {
          customerServiceNoteObjectName = ObjectConstants.RIDE_REQUEST;
          customerServiceNoteObjectId = trip.rideRequestDetail.id;
        }

        return this.customerServiceNoteService
          .getCustomerServiceNotesForObjectIdAndName(customerServiceNoteObjectId, customerServiceNoteObjectName)
          .pipe(
            map(
              notes => TripDetailActions.GetNotesSuccess({ customerServiceNoteDetails: notes }),
              catchError(error => of(TripDetailActions.GetNotesFail({ error })))
            )
          );
      })
    );
  });

  addNote$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.AddNote),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) => {
        if (!trip.driver) {
          action.customerServiceNotes.objectName = ObjectConstants.RIDE_REQUEST;
          action.customerServiceNotes.objectId = trip.rideRequestDetail.id;
        } else {
          action.customerServiceNotes.objectName = ObjectConstants.RESERVATION;
          action.customerServiceNotes.objectId = trip.id;
        }
        return this.customerServiceNoteService.createCustomerServiceNote(action.customerServiceNotes).pipe(
          map(note => TripDetailActions.AddNoteSuccess({ customerServiceNotes: note })),
          catchError(error => of(TripDetailActions.AddNoteFail({ error })))
        );
      })
    );
  });

  getChatMessages$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess, TripDetailActions.RefreshChatMessages, TripDetailActions.SendChatMessageSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(
        ([action, trip]) =>
          !isNullOrUndefined(trip) && this.authDataService.hasPermission({ any: [PermissionsConstants.CHAT_MESSAGE_READ], all: [] })
      ),
      switchMap(([action, trip]) =>
        trip?.driver
          ? this.reservationService.getChatMessagesForReservation(trip.id).pipe(
              map(chatMessages => TripDetailActions.GetChatMessagesSuccess({ payload: chatMessages })),
              catchError(error => of(TripDetailActions.GetChatMessagesFail({ error })))
            )
          : of(TripDetailActions.GetChatMessagesFail({ error: new ApplicationException('Could not get chats') }))
      )
    );
  });

  refreshChatMessages$ = interval(this.appConfiguration.listPollingSeconds * 1000).pipe(
    withLatestFrom(this.store$.select(getTrip)),
    filter(([action, trip]) => this.authDataService.isLoggedIn && !isNullOrUndefined(trip)),
    switchMap(() => of(TripDetailActions.RefreshChatMessages()))
  );

  // @Effect() refreshChatMessages$ = interval(this.appConfiguration.listPollingSeconds * 1000).pipe(
  //   withLatestFrom(this.store$.select(getTrip)),
  //   filter(([action, trip]) => this.authDataService.isLoggedIn && !isNullOrUndefined(trip)),
  //   switchMap(() => of(new RefreshChatMessages()))
  // );

  sendChatMessage$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.SendChatMessage),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.reservationService.sendChatMessage(trip.id, trip.driver.id, action.payload).pipe(
          map(sentMessage => TripDetailActions.SendChatMessageSuccess({ chatMessage: sentMessage })),
          catchError(error => of(TripDetailActions.SendChatMessageFail({ error })))
        )
      )
    );
  });

  // TODO: Remove this when chat is redesigned
  markSentChatMessageRead$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SendChatMessageSuccess),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) => {
          trip.numberOfReadChatMessages = trip.numberOfReadChatMessages + 1;
          return trip;
        })
      );
    },
    { dispatch: false }
  );

  goToRider$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToRider),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) =>
          this.router.navigate([
            `/riders/details/${trip.rideRequestDetail.organizationRider.id}/organization/${trip.rideRequestDetail.organizationRider.organizationId}`
          ])
        )
      );
    },
    { dispatch: false }
  );

  goToDriver$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToDriver),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) => this.router.navigate([`/driver-management/driver-detail/${trip.driver.id}`]))
      );
    },
    { dispatch: false }
  );

  goToLyftDetail$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToLyftDetail),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) =>
          this.router.navigate(['/lyft'], {
            queryParams: TabUtils.flattenParams({ ride: trip.rideRequestDetail.id }, LYFT_PARAMS_KEY)
          })
        )
      );
    },
    { dispatch: false }
  );

  goToRecoveryRide$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToRecoveryRide),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) => this.router.navigate([`/rides/rr-${trip.rideRequestDetail.childRideRequestId}`]))
      );
    },
    { dispatch: false }
  );

  goToOriginalRide$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToOriginalRide),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) => this.router.navigate([`/rides/rr-${trip.rideRequestDetail.parentRideRequestId}`]))
      );
    },
    { dispatch: false }
  );

  goToFareAdjustment$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.GoToFareAdjustment),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) => this.router.navigate([`/fare/adjustment/${trip.id}`]))
      );
    },
    { dispatch: false }
  );

  updateRideInfo$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.UpdateTripValue),
      filter(action => action.payload.getRideRequestInfo === true),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([action, state]) => {
        // Convert the vehicle type (Basic/WAV) to a proper service level.
        // Defaulting to door to door option for both ambulatory and non-ambulatory.
        const trip = state.trip;

        return this.organizationService
          .findNearestTransitProgramByLocations(
            trip.rideRequestDetail.organizationRider.organizationId,
            trip.rideRequestDetail.pickupLocation.locationId,
            trip.rideRequestDetail.dropoffLocation.locationId
          )
          .pipe(
            switchMap(transitProgramInfo =>
              this.transitProgramService
                .getTransitProgramRideRequestInfo(
                  transitProgramInfo.id,
                  trip.rideRequestDetail.pickupLocation.latitude,
                  trip.rideRequestDetail.pickupLocation.longitude,
                  trip.rideRequestDetail.dropoffLocation.latitude,
                  trip.rideRequestDetail.dropoffLocation.longitude,
                  trip.rideRequestDetail.billablePassengerCount,
                  this.getPickupDateForRideRequestInfo(trip),
                  this.getDropoffDateForRideRequestInfo(trip),
                  RideUtil.getD2DServiceLevelIdForRideRequestInfo(trip.rideRequestDetail.vehicleType, state.organizationServiceLevels)
                )
                .pipe(
                  map(rideRequestInfo =>
                    TripDetailActions.UpdateRideInfoSuccess({
                      payload: { rideRequestInfo: rideRequestInfo, transitProgramInfo: transitProgramInfo }
                    })
                  ),
                  catchError(error => of(TripDetailActions.UpdateRideInfoFail({ error })))
                )
            ),
            catchError(error => of(TripDetailActions.UpdateRideInfoFail({ error })))
          );
      })
    );
  });

  updateReservationStatus$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.UpdateReservationStatus),
      switchMap(action =>
        this.reservationService
          .updateReservationStatus(action.payload.reservationId, action.payload.statusName, action.payload.timestamp)
          .pipe(
            map(reservationDetail =>
              TripDetailActions.UpdateReservationStatusSuccess({
                reservationListItemVm: ReservationListItemVm.reservationListItemVmFromReservationDetail(reservationDetail)
              })
            ),
            catchError(error => of(TripDetailActions.UpdateReservationStatusFail({ error })))
          )
      )
    );
  });

  saveTrip$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SaveTrip),
        withLatestFrom(this.store$.select(getTripDetailState)),
        map(([action, state]) => {
          // TODO: Refactor this to a RideRequest for serialization
          const patchDict = {};
          let requestChanged = false;
          let incentiveChanged = false;
          let requestFeeChanged = false;

          if (state.trip.rideRequestDetail.riderNote !== state.uneditedTrip.rideRequestDetail.riderNote) {
            requestChanged = true;
            let riderNote = state.trip.rideRequestDetail.riderNote?.trim();

            if (riderNote?.length === 0) {
              riderNote = null;
            }

            patchDict['riderNote'] = riderNote;
          }

          if (state.trip.rideRequestDetail.pickupDate && state.uneditedTrip.rideRequestDetail.pickupDate) {
            if (state.trip.rideRequestDetail.pickupDate.getTime() !== state.uneditedTrip.rideRequestDetail.pickupDate.getTime()) {
              requestChanged = true;
              patchDict['pickupDate'] = state.trip.rideRequestDetail.pickupDate;
              patchDict['dropoffDate'] = state.trip.rideRequestDetail.dropoffDate;
              patchDict['dropoffDateProvided'] = state.trip.rideRequestDetail.dropoffDateProvided;
              patchDict['polylineId'] = state.editedPolylineId;
              patchDict['distanceEstimate'] = state.route.distance;
              patchDict['durationEstimate'] = state.route.duration;
              patchDict['requestFee'] = state.trip.rideRequestDetail.requestFee;
            }
          }

          // Checking dropoff date separately because on inital rollout there will be trips that have a pick-up date but do not have a drop-off date
          if (state.trip.rideRequestDetail.dropoffDate && state.uneditedTrip.rideRequestDetail.dropoffDate) {
            if (state.trip.rideRequestDetail.dropoffDate.getTime() !== state.uneditedTrip.rideRequestDetail.dropoffDate.getTime()) {
              requestChanged = true;
              patchDict['pickupDate'] = state.trip.rideRequestDetail.pickupDate;
              patchDict['dropoffDate'] = state.trip.rideRequestDetail.dropoffDate;
              patchDict['dropoffDateProvided'] = state.trip.rideRequestDetail.dropoffDateProvided;
              patchDict['polylineId'] = state.editedPolylineId;
              patchDict['distanceEstimate'] = state.route.distance;
              patchDict['durationEstimate'] = state.route.duration;
              patchDict['requestFee'] = state.trip.rideRequestDetail.requestFee;
            }
          }

          if (
            state.trip.rideRequestDetail.pickupLocation.locationId !== state.uneditedTrip.rideRequestDetail.pickupLocation.locationId ||
            state.trip.rideRequestDetail.pickupLocation.sublocationId !==
              state.uneditedTrip.rideRequestDetail.pickupLocation.sublocationId ||
            state.trip.rideRequestDetail.dropoffLocation.locationId !== state.uneditedTrip.rideRequestDetail.dropoffLocation.locationId ||
            state.trip.rideRequestDetail.dropoffLocation.sublocationId !==
              state.uneditedTrip.rideRequestDetail.dropoffLocation.sublocationId ||
            state.trip.rideRequestDetail.passengerCount !== state.uneditedTrip.rideRequestDetail.passengerCount ||
            state.trip.rideRequestDetail.billablePassengerCount !== state.uneditedTrip.rideRequestDetail.billablePassengerCount
          ) {
            requestChanged = true;
            patchDict['pickupLocation'] = state.trip.rideRequestDetail.pickupLocation;
            patchDict['dropoffLocation'] = state.trip.rideRequestDetail.dropoffLocation;
            patchDict['pickupDate'] = state.trip.rideRequestDetail.pickupDate;
            patchDict['dropoffDate'] = state.trip.rideRequestDetail.dropoffDate;
            patchDict['dropoffDateProvided'] = state.trip.rideRequestDetail.dropoffDateProvided;
            patchDict['polylineId'] = state.editedPolylineId;
            patchDict['distanceEstimate'] = state.route.distance;
            patchDict['durationEstimate'] = state.route.duration;
            patchDict['requestFee'] = state.trip.rideRequestDetail.requestFee;
            patchDict['passengerCount'] = state.trip.rideRequestDetail.passengerCount;
            patchDict['billablePassengerCount'] = state.trip.rideRequestDetail.billablePassengerCount;
          }

          if (state.trip.rideRequestDetail.incentive !== state.uneditedTrip.rideRequestDetail.incentive) {
            incentiveChanged = true;
          }

          if (state.trip.rideRequestDetail.requestFee !== state.uneditedTrip.rideRequestDetail.requestFee) {
            requestFeeChanged = true;
            patchDict['requestFee'] = state.trip.rideRequestDetail.requestFee;
          }

          return this.dialog
            .open(ConfirmationDialogComponent, {
              data: {
                title: requestFeeChanged ? 'The rider fee has changed' : 'Are you sure?',
                message: requestFeeChanged
                  ? `The rider will now be responsible for a fare of ${this.currencyPipe.transform(
                      patchDict['requestFee'],
                      'USD',
                      'symbol'
                    )}.
        Are you sure you want to save this change?`
                  : 'Are you sure you want to save this change?'
              }
            })
            .afterClosed()
            .subscribe(confirmed => {
              if (!confirmed) {
                return;
              } else {
                if (requestChanged || incentiveChanged || requestFeeChanged) {
                  if (requestChanged || requestFeeChanged) {
                    this.store$.dispatch(TripDetailActions.PatchRideRequest({ payload: patchDict }));
                  }
                  if (incentiveChanged) {
                    this.store$.dispatch(TripDetailActions.PatchIncentive({ payload: state.trip.rideRequestDetail.incentive }));
                  }
                } else {
                  this.store$.dispatch(TripDetailActions.CancelEditMode());
                }
              }
            });
        })
      );
    },
    { dispatch: false }
  );

  patchIncentive$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.PatchIncentive),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.rideRequestService
          .patchRideRequest(trip.rideRequestDetail.id, {
            incentive: action.payload
          })
          .pipe(
            map(rideRequest => TripDetailActions.PatchIncentiveSuccess({ rideRequest })),
            catchError(error => of(TripDetailActions.PatchIncentiveFail({ error })))
          )
      )
    );
  });

  patchRideRequest$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.PatchRideRequest),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.itineraryService.patchRideRequest(trip.rideRequestDetail.itineraryId, trip.rideRequestDetail.id, action.payload).pipe(
          map(rideRequest => TripDetailActions.PatchRideRequestSuccess({ rideRequest })),
          catchError(error => of(TripDetailActions.PatchRideRequestFail({ error })))
        )
      )
    );
  });

  cancelTrip$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.CancelTrip),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.rideCancellationService.cancelRide(trip).pipe(
          map(result => {
            return TripDetailActions.CancelTripSuccess({ recoveryItinerary: result });
          }),
          catchError(error => of(TripDetailActions.CancelTripFail({ error })))
        )
      )
    );
  });

  cancelSuccess$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.CancelTripSuccess),
      switchMap(action => {
        this.reservationEventService.rideRequestCancelled();
        if (!isNullOrUndefined(action.recoveryItinerary)) {
          return of(TripDetailActions.OpenRecoveryRideTab({ recoveryItinerary: action.recoveryItinerary }));
        }
        return of(TripDetailActions.RefreshTripDetail());
      })
    );
  });

  openRecoveryRideTab$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenRecoveryRideTab),
        map(action => {
          const recoveryTrip = new Trip();
          recoveryTrip.rideRequestId = action.recoveryItinerary.rideRequests[0].id;
          this.store$.dispatch(TripDetailActions.OpenTripDetailTab({ payload: { trip: recoveryTrip } }));
        })
      );
    },
    { dispatch: false }
  );

  dispatchToLyft$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.DispatchToLyft),
      withLatestFrom(this.store$.select(getTrip)),
      switchMap(([action, trip]) =>
        this.lyftService.dispatchRideRequest(trip.rideRequestDetail.id).pipe(
          map(success => {
            this.snackbar.open('Successfully dispatched to Lyft', null, { duration: 3000 });
            return TripDetailActions.DispatchToLyftSuccess();
          }),
          catchError(error => of(TripDetailActions.DispatchToLyftFail({ error })))
        )
      )
    );
  });

  selectDriver$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SelectDriver),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) =>
          this.dialog
            .open(SelectDriverDialogComponent, {
              data: {
                rideRequest: trip.rideRequestDetail,
                vehicleType: trip.rideRequestDetail.vehicleType
              }
            })
            .afterClosed()
            .subscribe(data => {
              if (!isNullOrUndefined(data)) {
                let restOperation: Observable<Reservation | Journey>;

                if (!isNullOrUndefined(data.interestedDriver)) {
                  restOperation = this.tripService.selectDriver(data.interestedDriver, trip);
                }

                if (!isNullOrUndefined(data.journeyInterestedDriver)) {
                  restOperation = this.tripService.selectDriver(data.journeyInterestedDriver, trip);
                }

                restOperation.subscribe({
                  complete: () => {
                    this.store$.dispatch(TripDetailActions.RefreshTripDetail());
                  },
                  error: error => {
                    const errorBuilder = this.tripService.handleSelectionErrorMessages(error.message);

                    const errMessage = errorBuilder.message;
                    const refresh = errorBuilder.refresh;

                    this.dialog
                      .open(AlertDialogComponent, {
                        data: {
                          title: 'Error',
                          message: errMessage
                        }
                      })
                      .afterClosed()
                      .subscribe(() => {
                        if (refresh) {
                          this.store$.dispatch(TripDetailActions.SelectDriver());
                        }
                      });
                  }
                });
              }
            })
        )
      );
    },
    { dispatch: false }
  );

  selectProvider$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SelectProvider),
        withLatestFrom(this.store$.select(getTrip)),
        map(([action, trip]) =>
          this.dialog
            .open(SelectProviderDialogComponent, {
              data: {
                rideRequest: trip.rideRequestDetail,
                vehicleType: trip.rideRequestDetail.vehicleType
              }
            })
            .afterClosed()
            .subscribe(data => {
              if (!isNullOrUndefined(data) && !isNullOrUndefined(data.providerSelectionDetail)) {
                let restOperation: Observable<Reservation>;

                if (!isNullOrUndefined(data.providerSelectionDetail)) {
                  restOperation = this.tripService.selectProvider(data.providerSelectionDetail, trip);
                }

                restOperation.subscribe({
                  complete: () => {
                    this.store$.dispatch(TripDetailActions.RefreshTripDetail());
                  },
                  error: error => {
                    const errorBuilder = this.tripService.handleSelectionErrorMessages(error.message);

                    const errMessage = errorBuilder.message;
                    const refresh = errorBuilder.refresh;

                    this.dialog
                      .open(AlertDialogComponent, {
                        data: {
                          title: 'Error',
                          message: errMessage
                        }
                      })
                      .afterClosed()
                      .subscribe(() => {
                        if (refresh) {
                          this.store$.dispatch(TripDetailActions.SelectProvider());
                        }
                      });
                  }
                });
              }
            })
        )
      );
    },
    { dispatch: false }
  );

  openDriverFareCalculationHistoryDialog$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenDriverFareCalculationHistoryDialog),
        withLatestFrom(this.store$.select(getFareReviewDetail)),
        map(([_, fareReviewDetail]) => {
          this.dialog.open(DriverFareCalculationHistoryDailogComponent, {
            data: { driverFareCalculations: fareReviewDetail.driverFareCalculations }
          });
        })
      );
    },
    { dispatch: false }
  );

  openFareAdjustmentNotesDialog$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenFareAdjustmentNotesDialog),
        map(action => {
          this.dialog.open(TripNotesDialogComponent, {
            data: {
              fareReviewNotes: this.store$.select(getFareReviewNotes)
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  openFareAndRatesDetailsDialog$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.OpenFareAndRatesDetailsDialog),
        map(action => {
          this.dialog.open(FareAndRatesDetailsDailogComponent, {
            data: action.estimateActualFareCalulation
          });
        })
      );
    },
    { dispatch: false }
  );

  showFareReviewEditMode$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.ShowFareReviewEditMode),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([_, state]) => {
        const fareReviewStatusUpdate = new FareReviewStatusUpdate();
        fareReviewStatusUpdate.reservationId = state.trip.id;
        fareReviewStatusUpdate.status = FareReviewStatusConstants.IN_PROGRESS;
        if (state.fareReviewStatus.executionPlatform === ExecutionPlatform.UZURV360) {
          return this.fareService.updateFareReviewStatus(fareReviewStatusUpdate).pipe(
            map(
              driverFairCalculation => TripDetailActions.ShowFareReviewEditModeSuccess({}),
              catchError(error => of(TripDetailActions.ShowFareReviewEditModeFail({ error })))
            )
          );
        } else {
          const lyftDispatchRideFareReviewStatusUpdate = new LyftDispatchRideFareReviewStatusUpdate();
          lyftDispatchRideFareReviewStatusUpdate.lyftDispatchRideId = state.lyftDispatchRide.id;
          lyftDispatchRideFareReviewStatusUpdate.status = FareReviewStatusConstants.IN_PROGRESS;

          return this.fareService
            .updateFareReviewStatusLyft(state.lyftDispatchRide.id, lyftDispatchRideFareReviewStatusUpdate)
            .pipe(map(() => TripDetailActions.ShowLyftReviewEditModeSuccess({})));
        }
      })
    );
  });

  calculateEstimateAdjustment$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetFareAdjustmentEstimate),
      debounceTime(1000),
      switchMap(action =>
        this.fareService
          .estimateFareAdjustment(action.fareAdjustment)
          .pipe(
            map(driverFareCalulation =>
              TripDetailActions.GetFareAdjustmentEstimateSuccess({ estimateDriverFareCalculation: driverFareCalulation })
            )
          )
      )
    );
  });

  validateProviderCommissionAdjustment$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(TripDetailActions.GetFareAdjustmentEstimateSuccess),
        withLatestFrom(this.store$.select(getTripDetailState)),
        map(([action, state]) => {
          const originalEstimate = state.fareReviewDetail.driverFareCalculations.find(dfc => dfc.active && dfc.estimate);
          if (isNullOrUndefined(originalEstimate)) {
            return;
          }

          const originalCommission: number = originalEstimate.totalProviderCommission;
          const adjustedCommission: number = action.estimateDriverFareCalculation.totalProviderCommission;

          if (originalCommission <= 0) {
            return;
          }

          const isWav: boolean = state.trip.rideRequestDetail.vehicleType.code === VehicleTypeConstants.WAV;
          const thresholds: ProviderCommissionAdjustmentThresholds =
            this.providerCommissionAdjustmentValidationService.getProviderCommissionAdjustmentThresholds(isWav);
          const overageDollars: number = adjustedCommission - originalCommission;
          const overagePercent: number = (overageDollars / originalCommission) * 100;

          const deviationState: AdjustedProviderCommissionDeviationState =
            this.providerCommissionAdjustmentValidationService.getDeviationState(overageDollars, overagePercent, thresholds);

          this.store$.dispatch(
            TripDetailActions.SetAdjustedProviderCommissionDeviationState({ adjustedProviderCommissionDeviationState: deviationState })
          );
        })
      ),
    { dispatch: false }
  );

  cancelFareAdjustment$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.CancelFareAdjustment),
        withLatestFrom(this.store$.select(getTripDetailState)),
        map(([_, state]) => {
          if (state.fareReviewStatus.fareReviewStatus === FareReviewStatusConstants.IN_PROGRESS) {
            this.store$.dispatch(TripDetailActions.UpdateFareReviewStatusRequired());
          } else {
            this.store$.dispatch(TripDetailActions.CancelFareAdjustmentSuccess({}));
          }
        })
      );
    },
    { dispatch: false }
  );

  updateFareReviewStatusRequired$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.UpdateFareReviewStatusRequired),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([_, state]) => {
        const fareReviewStatusUpdate = new FareReviewStatusUpdate();
        fareReviewStatusUpdate.status = FareReviewStatusConstants.REQUIRED;
        fareReviewStatusUpdate.reservationId = state.trip.id;

        return this.fareService
          .updateFareReviewStatus(fareReviewStatusUpdate)
          .pipe(map(() => TripDetailActions.CancelFareAdjustmentSuccess({})));
      })
    );
  });

  submitFareAdjustment$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.SubmitFareAdjustment),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([action, state]) => {
        if (state.fareReviewStatus.fareReviewStatus === FareReviewStatusConstants.IN_PROGRESS) {
          let fareStatus: string = '';
          const fareReviewStatusUpdate = new FareReviewStatusUpdate();
          state.fareReviewDetail.driverFareCalculations.forEach(fareCalc => {
            if (fareCalc.active && !fareCalc.estimate) {
              if (
                action.fareAdjustment.distanceMiles === fareCalc.distanceMiles &&
                action.fareAdjustment.driverIncentive === fareCalc.providerIncentive &&
                action.fareAdjustment.driverTollReimbursement === fareCalc.tollReimbursement &&
                action.fareAdjustment.durationSeconds / 60 === fareCalc.durationInMinutes
              ) {
                fareStatus = FareReviewStatusConstants.COMPLETED_NOT_ADJUSTED;
              } else {
                fareReviewStatusUpdate.fareAdjustment = action.fareAdjustment;
                fareStatus = FareReviewStatusConstants.COMPLETED_ADJUSTED;
              }
            }
          });
          fareReviewStatusUpdate.status = fareStatus;
          fareReviewStatusUpdate.reservationId = state.trip.id;
          return this.fareService.updateFareReviewStatus(fareReviewStatusUpdate).pipe(
            map(() => TripDetailActions.SubmitFareAdjustmentSuccess({})),
            catchError(error => of(TripDetailActions.SubmitFareAdjustmentFail({ error })))
          );
        } else {
          return this.fareService.adjustFare(action.fareAdjustment).pipe(map(() => TripDetailActions.SubmitFareAdjustmentSuccess({})));
        }
      })
    );
  });

  cancelLyftReview$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.CancelLyftReview),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([_, state]) => {
        if (state.fareReviewStatus.fareReviewStatus === FareReviewStatusConstants.IN_PROGRESS) {
          const lyftDispatchRideFareReviewStatusUpdate = new LyftDispatchRideFareReviewStatusUpdate();
          lyftDispatchRideFareReviewStatusUpdate.lyftDispatchRideId = state.lyftDispatchRide.id;
          lyftDispatchRideFareReviewStatusUpdate.status = FareReviewStatusConstants.REQUIRED;
          return this.fareService
            .updateFareReviewStatusLyft(state.lyftDispatchRide.id, lyftDispatchRideFareReviewStatusUpdate)
            .pipe(map(() => TripDetailActions.CancelLyftReviewSuccess({})));
        } else {
          return of(TripDetailActions.CancelLyftReviewSuccess({}));
        }
      })
    );
  });

  submitLyftReview$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.SubmitLyftReview),
      switchMap(action =>
        this.fareService
          .updateFareReviewStatusLyft(
            action.lyftDispatchRideFareReviewStatusUpdate.lyftDispatchRideId,
            action.lyftDispatchRideFareReviewStatusUpdate
          )
          .pipe(
            map(() => TripDetailActions.SubmitLyftReviewSuccess({})),
            catchError(error => of(TripDetailActions.SubmitLyftReviewFail({ error })))
          )
      )
    );
  });

  getItinerary$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([action, trip]) => !isNullOrUndefined(trip)),
      switchMap(([action, trip]) =>
        this.itineraryService.findById(trip.rideRequestDetail.itineraryId).pipe(
          map(itinerary => TripDetailActions.GetItinerarySuccess({ itinerary })),
          catchError(error => of(TripDetailActions.GetItineraryFail({ error })))
        )
      )
    );
  });

  goToItineraryRide$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GoToItineraryRide),
      switchMap(action => {
        const trip = new Trip();
        // This is temporary to prevent opening duplicate tabs. In the future, we could use the status of a ride
        // if added to the itinerary ride request data
        return this.rideRequestService.findById(action.id).pipe(
          switchMap(rideRequest => {
            if (rideRequest.accepted) {
              return this.rideRequestService.findActiveReservationByRideRequestId(action.id).pipe(
                map(activeReservation => {
                  trip.reservationId = activeReservation.id;
                  trip.driverName = 'Driver';
                  return TripDetailActions.OpenTripDetailTab({ payload: { trip: trip } });
                })
              );
            } else {
              trip.rideRequestId = action.id;
            }
            return of(TripDetailActions.OpenTripDetailTab({ payload: { trip: trip } }));
          })
        );
      })
    );
  });

  submitFareAdjustmentSuccess$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.SubmitFareAdjustmentSuccess),
        map(() => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Success',
              message: 'Fare review accepted'
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  getOrganizationServiceLevels$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([_, trip]) => !isNullOrUndefined(trip)),
      switchMap(([_, trip]) =>
        this.organizationService.listOrganizationServiceLevels(trip.rideRequestDetail.transitProgramInfo.organization.id).pipe(
          map(organizationServiceLevels => TripDetailActions.GetOrganizationServiceLevelsSuccess({ organizationServiceLevels })),
          catchError(error => of(TripDetailActions.GetOrganizationServiceLevelsFail({ error })))
        )
      )
    );
  });

  getOrganizationRiderServiceLevels$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([_, trip]) => !isNullOrUndefined(trip)),
      switchMap(([_, trip]) =>
        this.organizationRiderService.listOrganizationRiderServiceLevels(trip.rideRequestDetail.organizationRider.id).pipe(
          map(organizationRiderServiceLevels =>
            TripDetailActions.GetOrganizationRiderServiceLevelsSuccess({ organizationServiceLevels: organizationRiderServiceLevels })
          ),
          catchError(error => of(TripDetailActions.GetOrganizationRiderServiceLevelsFail({ error })))
        )
      )
    );
  });

  getPaymentMethods$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(([_, trip]) => !isNullOrUndefined(trip) && trip.isCompleted && trip.driver.emailAddress !== 'lyft_dispatch_driver'),
      switchMap(() =>
        this.paymentAccountService.getPaymentMethods().pipe(
          map(paymentMethods => TripDetailActions.GetPaymentMethodsSuccess({ paymentMethods })),
          catchError(error => of(TripDetailActions.GetPaymentMethodsFail({ error })))
        )
      )
    );
  });

  getRiderFares$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTrip)),
      filter(
        ([_, trip]) =>
          !isNullOrUndefined(trip) &&
          trip.isReservation &&
          ReservationStatusOrder[trip.reservationStatus.name] >= ReservationStatusOrder[ReservationStatusNameConstants.PICK_UP_ARRIVED] &&
          trip.driver.emailAddress !== 'lyft_dispatch_driver'
      ),
      switchMap(([_, trip]) =>
        this.reservationService.getRiderFaresByReservationId(trip.id).pipe(
          map(rideFares => TripDetailActions.GetRiderFaresSuccess({ rideFares })),
          catchError(error => of(TripDetailActions.GetRiderFaresFail({ error })))
        )
      )
    );
  });

  showLoadingSpinner$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.UpdateReservationStatus),
        map(action => {
          // this.spinnerService.show();
        })
      );
    },
    { dispatch: false }
  );

  hideLoadingSpinner$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(TripDetailActions.UpdateReservationStatusSuccess, TripDetailActions.UpdateReservationStatusFail),
        map(action => {
          // this.spinnerService.hide();
        })
      );
    },
    { dispatch: false }
  );

  error$ = createEffect(
    () => {
      return this.action$.pipe(
        ofType(
          TripDetailActions.GetTripDetailFail,
          TripDetailActions.UpdateRideInfoFail,
          TripDetailActions.UpdateReservationStatusFail,
          TripDetailActions.PatchIncentiveFail,
          TripDetailActions.PatchRideRequestFail,
          TripDetailActions.DispatchToLyftFail,
          TripDetailActions.GetFareReviewDetailFail,
          TripDetailActions.SubmitLyftReviewFail,
          TripDetailActions.FavoriteDriverFail,
          TripDetailActions.RemoveFavoritedDriverFail,
          TripDetailActions.IgnoreDriverFail,
          TripDetailActions.RemoveFavoritedDriverFail,
          TripDetailActions.SubmitRiderFareFail,
          TripDetailActions.ChangeDriverVehicleFail,
          TripDetailActions.GetEligibleVehiclesForReservationFail,
          TripDetailActions.SubmitFareAdjustmentFail
        ),
        map(action => {
          if (action.error.message.includes('pickupDate must be in the future')) {
            action.error = new ApplicationException('Please select a future pickup date');
          }

          if (action.error.message.includes('100 characters')) {
            action.error.message = 'The trip notes field must be less than 100 characters for transit programs with Lyft fall back enabled';
          }

          if (action.error.message.includes('must be different')) {
            action.error.message = 'The pickup and dropoff points must be different';
          }

          if (action.error.message.includes('Error: null')) {
            action.error.message = 'Could not find ride';
          }

          if (RiderFareErrorConstants.TICKET_NUMBER_EXCEEDS_MAX_USAGE.test(action.error.message)) {
            const match = action.error.message.match(RiderFareErrorConstants.TICKET_NUMBER_EXCEEDS_MAX_USAGE);

            action.error.message = `Ticket with number ${match[1]} has exceeded it maximum usage. Please remove this ticket.`;
          }

          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: this.errorTranslationService.translateErrorMessage(action.error.message)
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  editRiderFare$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(TripDetailActions.EditRiderFare),
        map(action => {
          this.dialog.open(RiderFareCollectionDialogComponent, {
            data: action.reservationListItemVm
          });
        })
      ),
    { dispatch: false }
  );

  submitRiderFare$ = createEffect(() =>
    this.action$.pipe(
      ofType(TripDetailActions.SubmitRiderFare),
      switchMap(action =>
        this.reservationService.saveRiderFares(action.payload.reservationId, action.payload.riderFareArray).pipe(
          map(riderFares => {
            action.payload.dialogRef.close();

            return TripDetailActions.SubmitRiderFareSuccess({
              payload: {
                reservationListItemVm: action.payload.reservationListItemVm,
                riderFares: riderFares
              }
            });
          }),
          catchError(error => of(TripDetailActions.SubmitRiderFareFail({ error })))
        )
      )
    )
  );

  submitRiderFareSuccess$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(TripDetailActions.SubmitRiderFareSuccess),
        map(action => {
          if (action.payload.reservationListItemVm.fromStatus) {
            this.dialog.open(AdjustReservationStatusDialogComponent, {
              data: {
                reservation: action.payload.reservationListItemVm
              }
            });
          }
        })
      ),
    { dispatch: false }
  );

  getEligibleVehicles$ = createEffect(() =>
    this.action$.pipe(
      ofType(TripDetailActions.GetTripDetailSuccess),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(
        ([_, state]) =>
          !isNullOrUndefined(state.trip?.driver) &&
          this.authDataService.hasPermission({ any: [PermissionsConstants.RESERVATION_UPDATE], all: [] })
      ),
      switchMap(([_, state]) => {
        return this.reservationService.getEligibleVehiclesForReservationById(state.trip.id).pipe(
          map(vehicles => {
            return TripDetailActions.GetEligibleVehiclesForReservationSuccess({ eligibleVehicles: vehicles });
          }),
          catchError(error => of(TripDetailActions.GetEligibleVehiclesForReservationFail({ error })))
        );
      })
    )
  );

  changeDriverVehicle$ = createEffect(() =>
    this.action$.pipe(
      ofType(TripDetailActions.ChangeDriverVehicle),
      withLatestFrom(this.store$.select(getTripDetailState)),
      filter(([_, state]) => !isNullOrUndefined(state.trip?.driver)),
      switchMap(([action, state]) => {
        const containsVehicle = state.eligibleVehicles.some(vehicle => vehicle.id === action.payload.id);
        if (containsVehicle) {
          return this.reservationService.updateReservationVehicle(state.trip.id, action.payload.id).pipe(
            map(() => TripDetailActions.ChangeDriverVehicleSuccess({ payload: action.payload })),
            catchError(error => of(TripDetailActions.ChangeDriverVehicleFail({ error })))
          );
        }
      })
    )
  );

  startNonacceptedRides$ = createEffect(() =>
    this.action$.pipe(
      ofType(TripDetailActions.StartNonacceptedRides),
      withLatestFrom(this.store$.select(getNonacceptedRidesParams)),
      switchMap(() => {
        return [TripDetailActions.FetchNonacceptedRides()];
      })
    )
  );

  fetchNonAcceptedRides$ = createEffect(() => {
    return this.action$.pipe(
      ofType(TripDetailActions.FetchNonacceptedRides, TripDetailActions.SetNonacceptedRidesPagination),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([action, state]) => {
        return this.rideRequestService
          .listNonacceptedRideRequests(state.nonacceptedRidesParams.pageIndex, state.nonacceptedRidesParams.pageSize)
          .pipe(
            map(nonAcceptedRides => TripDetailActions.FetchNonAcceptedRidesSuccess({ nonacceptedRides: nonAcceptedRides })),
            catchError(error => of(TripDetailActions.FetchNonAcceptedRidesFail({ error })))
          );
      })
    );
  });

  getRiderIdCardImage$ = createEffect(() =>
    this.action$.pipe(
      ofType(TripDetailActions.GetRiderIdCardImage),
      withLatestFrom(this.store$.select(getTripDetailState)),
      switchMap(([_, state]) =>
        this.reservationService.getRiderIdCardImage(state.trip.id).pipe(
          map(
            fileData =>
              TripDetailActions.GetRiderIdCardImageSuccess({
                safeUrl: fileData && fileData.getDataURLString() ? this.safeUrlPipe.transform(fileData.getDataURLString()) : null
              }),
            catchError(error => of(TripDetailActions.GetRiderIdCardImageFail({ error })))
          )
        )
      )
    )
  );

  constructor(
    private action$: Actions,
    private store$: Store<State<TripDetailState>>,
    private dialog: MatDialog,
    private router: Router,
    private authDataService: AuthDataService,
    private reservationService: ReservationService,
    private reservationEventService: ReservationEventService,
    private rideRequestService: RideRequestService,
    private rideCancellationService: RideCancellationService,
    private organizationService: OrganizationService,
    private organizationRiderService: OrganizationRiderService,
    private dotsDriverService: DotsDriverService,
    private transitProgramService: TransitProgramService,
    private itineraryService: ItineraryService,
    private safeUrlPipe: SafeUrlPipe,
    private currencyPipe: CurrencyPipe,
    private tripService: TripService,
    private customerServiceNoteService: CustomerServiceNoteService,
    private lyftService: LyftService,
    private fareService: FareService,
    private paymentAccountService: PaymentAccountService,
    private snackbar: MatSnackBar,
    private errorTranslationService: ErrorTranslationService,
    private ruleValueService: RuleValueService,
    private providerCommissionAdjustmentValidationService: ProviderCommissionAdjustmentValidationService,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration
  ) {}

  private getRideByUrlFragment(url: string): { rideTypeString: 'rr' | 'res'; rideId: number } {
    const urlComponents = url.split('/');
    const rideTypeString = urlComponents[urlComponents.length - 1].split('-')[0] as 'rr' | 'res';
    const rideId = +url.split('-')[1].split('?')[0];
    return { rideTypeString, rideId };
  }

  private getPickupDateForRideRequestInfo(trip: ReservationListItemVm): Date {
    if (trip.rideRequestDetail.rideRequestType.onDemand) {
      return null;
    }

    if (trip.rideRequestDetail.dropoffDateProvided) {
      return null;
    }

    if (isNullOrUndefined(trip.rideRequestDetail.pickupDate)) {
      return null;
    }

    return trip.rideRequestDetail.pickupDate;
  }

  private getDropoffDateForRideRequestInfo(trip: ReservationListItemVm): Date {
    if (trip.rideRequestDetail.rideRequestType.onDemand) {
      return null;
    }

    if (!trip.rideRequestDetail.dropoffDateProvided) {
      return null;
    }

    if (isNullOrUndefined(trip.rideRequestDetail.dropoffDate)) {
      return null;
    }

    return trip.rideRequestDetail.dropoffDate;
  }
}
